/**
 * Tenant provisioning for MindPeer staff.
 *
 * Functional only — no design pass (Thanasis, 7 Sep). The job is that a
 * MindPeer admin can take a customer from nothing to a working first session
 * without an engineer touching the database, so what matters here is that the
 * two writes are reachable and that their outcome is legible.
 *
 * It lives in the auth portal rather than in the product's own admin area for a
 * plain mechanical reason: these endpoints authenticate with the auth session
 * cookie, which is `SameSite=Lax` and therefore not sent on a cross-origin
 * request from the module. Putting the screen here needs no token plumbing at
 * all, and the auth service is where organizations and invitations actually
 * live.
 */
import { useCallback, useEffect, useState } from 'react';
import type { AdminOrganization, InvitationSummary } from '@mindpeer/auth-shared';
import { Alert, Button, Card, CardHeader, Field, Input, Spinner } from '@/components/ui';
import { useAuth } from '@/hooks/use-auth';
import { ApiError, api } from '@/lib/api';

const ASSIGNABLE_ROLES = ['admin', 'owner', 'member', 'viewer'] as const;

function errorMessage(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback;
}

export function AdminOrganizationsPage() {
  const { user } = useAuth();
  const canProvision = user?.platformRoles.includes('platform_admin') ?? false;

  const [organizations, setOrganizations] = useState<AdminOrganization[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const { organizations: rows } = await api.adminOrganizations();
      setOrganizations(rows);
      setLoadError(null);
    } catch (error) {
      setLoadError(errorMessage(error, 'Could not load organizations.'));
    }
  }, []);

  useEffect(() => {
    document.title = 'Organizations | MindPeer Auth';
    void load();
  }, [load]);

  /* ---- Create ---- */
  const [name, setName] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [created, setCreated] = useState<
    { organization: AdminOrganization; entitledApplicationId: string } | null
  >(null);

  const createOrganization = async () => {
    setCreating(true);
    setCreateError(null);
    try {
      const { organization, entitledApplicationId } = await api.createOrganization({ name });
      setCreated({ organization, entitledApplicationId });
      setName('');
      setSelected(organization);
      await load();
    } catch (error) {
      setCreateError(errorMessage(error, 'Could not create the organization.'));
    } finally {
      setCreating(false);
    }
  };

  /* ---- Invite ---- */
  const [selected, setSelected] = useState<AdminOrganization | null>(null);
  const [invitations, setInvitations] = useState<InvitationSummary[]>([]);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<string>('admin');
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [lastAcceptUrl, setLastAcceptUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const loadInvitations = useCallback(async (organizationId: string) => {
    try {
      const { invitations: rows } = await api.organizationInvitations(organizationId);
      setInvitations(rows);
    } catch {
      setInvitations([]);
    }
  }, []);

  useEffect(() => {
    if (selected) void loadInvitations(selected.id);
    else setInvitations([]);
  }, [selected, loadInvitations]);

  const invite = async () => {
    if (!selected) return;
    setInviting(true);
    setInviteError(null);
    setCopied(false);
    try {
      const { invitation } = await api.createInvitation(selected.id, {
        email,
        roles: [role],
      });
      // Surfaced because local and staging deployments often have no mail
      // transport configured: without the link a provisioning run looks
      // successful while the invitee never hears anything.
      setLastAcceptUrl(invitation.acceptUrl ?? null);
      setEmail('');
      await loadInvitations(selected.id);
      await load();
    } catch (error) {
      setInviteError(errorMessage(error, 'Could not send the invitation.'));
    } finally {
      setInviting(false);
    }
  };

  const revoke = async (invitationId: string) => {
    if (!selected) return;
    try {
      await api.revokeInvitation(selected.id, invitationId);
      await loadInvitations(selected.id);
    } catch (error) {
      setInviteError(errorMessage(error, 'Could not revoke the invitation.'));
    }
  };

  if (!canProvision) {
    return (
      <Card>
          <CardHeader
            title="Organizations"
            description="Provisioning a customer organization requires a platform administrator."
          />
        <Alert tone="warning">
          Your account does not hold the <code>platform_admin</code> role.
        </Alert>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
        {/* ---- Create an organization ---- */}
        <Card>
          <CardHeader
            title="New organization"
            description="Creates the tenant, sets it to invitation-only, and entitles it for MindPeer."
          />
          <form
            className="space-y-4"
            onSubmit={(event) => {
              event.preventDefault();
              void createOrganization();
            }}
          >
            {createError && <Alert tone="error">{createError}</Alert>}
            {created && (
              <Alert tone="success">
                Created <strong>{created.organization.name}</strong> ({created.organization.slug}),
                entitled for <code>{created.entitledApplicationId}</code>. Invite its administrator
                below.
              </Alert>
            )}
            <Field
              label="Organization name"
              htmlFor="org-name"
              hint="The URL identifier is derived from this."
            >
              <Input
                id="org-name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="Acme Corp"
                required
              />
            </Field>
            <Button type="submit" loading={creating} disabled={!name.trim()}>
              Create organization
            </Button>
          </form>
        </Card>

        {/* ---- Existing organizations ---- */}
        <Card>
          <CardHeader
            title="Organizations"
            description="Pick one to invite people into it."
          />
          {loadError && <Alert tone="error">{loadError}</Alert>}
          {organizations === null && !loadError ? (
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Spinner className="size-4" /> Loading…
            </div>
          ) : null}
          {organizations?.length === 0 && (
            <p className="text-sm text-slate-500">No organizations yet.</p>
          )}
          {organizations && organizations.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
                    <th className="py-2 pr-4">Name</th>
                    <th className="py-2 pr-4">Identifier</th>
                    <th className="py-2 pr-4">Join policy</th>
                    <th className="py-2 pr-4">Members</th>
                    <th className="py-2" />
                  </tr>
                </thead>
                <tbody>
                  {organizations.map((org) => (
                    <tr key={org.id} className="border-b border-slate-100">
                      <td className="py-2 pr-4 font-medium">{org.name}</td>
                      <td className="py-2 pr-4 font-mono text-xs text-slate-500">{org.slug}</td>
                      <td className="py-2 pr-4 text-slate-500">{org.joinPolicy}</td>
                      <td className="py-2 pr-4 tabular-nums">{org.memberCount}</td>
                      <td className="py-2">
                        <Button
                          variant="secondary"
                          onClick={() => setSelected(org)}
                          disabled={selected?.id === org.id}
                        >
                          {selected?.id === org.id ? 'Selected' : 'Select'}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* ---- Invite ---- */}
        {selected && (
          <Card>
            <CardHeader
              title={`Invite to ${selected.name}`}
              description="The invitee receives a link to accept, sign in, and land in this organization."
            />
            <form
              className="space-y-4"
              onSubmit={(event) => {
                event.preventDefault();
                void invite();
              }}
            >
              {inviteError && <Alert tone="error">{inviteError}</Alert>}
              {lastAcceptUrl && (
                <Alert tone="success">
                  <p>Invitation sent. If mail is not configured here, share this link:</p>
                  <p className="mt-2 break-all font-mono text-xs">{lastAcceptUrl}</p>
                  <Button
                    className="mt-2"
                    variant="secondary"
                    onClick={() => {
                      void navigator.clipboard?.writeText(lastAcceptUrl);
                      setCopied(true);
                    }}
                  >
                    {copied ? 'Copied' : 'Copy link'}
                  </Button>
                </Alert>
              )}
              <Field label="Email address" htmlFor="invite-email">
                <Input
                  id="invite-email"
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="boss@acme.com"
                  required
                />
              </Field>
              <Field
                label="Role"
                htmlFor="invite-role"
                hint="A tenant's first invitee should be an admin so they can invite their own team."
              >
                <select
                  id="invite-role"
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  value={role}
                  onChange={(event) => setRole(event.target.value)}
                >
                  {ASSIGNABLE_ROLES.map((value) => (
                    <option key={value} value={value}>
                      {value}
                    </option>
                  ))}
                </select>
              </Field>
              <Button type="submit" loading={inviting} disabled={!email.trim()}>
                Send invitation
              </Button>
            </form>

            <div className="mt-6">
              <h3 className="text-sm font-semibold text-slate-700">Pending invitations</h3>
              {invitations.length === 0 ? (
                <p className="mt-1 text-sm text-slate-500">None outstanding.</p>
              ) : (
                <ul className="mt-2 space-y-2">
                  {invitations.map((invitation) => (
                    <li
                      key={invitation.id}
                      className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm"
                    >
                      <span>
                        {invitation.email}{' '}
                        <span className="text-slate-500">({invitation.roles.join(', ')})</span>
                      </span>
                      <span className="flex items-center gap-3">
                        <span className="text-xs text-slate-500">
                          expires {new Date(invitation.expiresAt).toLocaleDateString()}
                        </span>
                        <Button variant="danger" onClick={() => void revoke(invitation.id)}>
                          Revoke
                        </Button>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </Card>
      )}
    </div>
  );
}
